import { useEffect, useState, useRef } from 'react';

interface UseCountUpOptions {
  /** Target number to animate to */
  end: number;
  /** Starting number (default 0) */
  start?: number;
  /** Duration in ms (default 800) */
  duration?: number;
  /** Delay before starting in ms (default 0) */
  delay?: number;
  /** Number of decimal places (default 0) */
  decimals?: number;
  /** Whether the animation should run (tie to useInView) */
  enabled?: boolean;
  /** Change this to force re-animation (e.g. carousel index) */
  trigger?: number | string;
}

/**
 * Easing: decelerate (ease-out cubic) — starts fast, slows at the end.
 * Feels like a number "landing" on the final value.
 */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Animates a number from `start` to `end` with easing.
 * Returns the current display value as a string.
 *
 * Pass `trigger` to re-run the animation when the carousel changes.
 */
export function useCountUp({
  end,
  start = 0,
  duration = 800,
  delay = 0,
  decimals = 0,
  enabled = true,
  trigger,
}: UseCountUpOptions): string {
  const [value, setValue] = useState(start);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Respect reduced motion
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setValue(end);
      return;
    }

    if (!enabled) {
      setValue(start);
      return;
    }

    // Reset to start when trigger changes
    setValue(start);

    const timeout = setTimeout(() => {
      const startTime = performance.now();

      const animate = (now: number): void => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(progress);
        const current = start + (end - start) * eased;

        setValue(current);

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        }
      };

      rafRef.current = requestAnimationFrame(animate);
    }, delay);

    return (): void => {
      clearTimeout(timeout);
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, end, start, duration, delay, trigger]);

  return value.toFixed(decimals);
}
